import React, { useState, useEffect } from 'react';
import IsoDateCalendarPicker from './planner/IsoDateCalendarPicker';
import {
  restockChannelForProduct,
  getRestockDate,
  setManualRestockEstimate,
  clearManualRestockEstimate
} from '../utils/restockDatesClient';

/**
 * ETA row on the Products card (2026-08-22): the customer-facing Estimated
 * Restock Date.
 *
 *   ETA: 4 Sep 2026 ✓   ← green = on track, ✓ = live on Shopify
 *   ETA: 1 Sep 2026     ← red = past, and an active PO is still behind it
 *   ETA: click to set   ← no source yet, click sets a manual estimate
 *
 * Precedence is server-owned (PO > manual > preorder); PO/preorder-sourced
 * dates are read-only here.
 *
 * 2026-08-29, two changes:
 *  - The editor is the shared Polaris calendar, NOT `<input type="date">`.
 *    The native control commits on the DOM `input` event, which is exactly
 *    what React's onChange listens to, so moving through the browser's own
 *    picker saved a date nobody chose, and the input's onBlur tore the editor
 *    down mid-pick. The calendar separates navigation (onMonthChange) from
 *    selection (onChange), so only a clicked day writes.
 *  - The row rides beside the variant line instead of claiming a band of its
 *    own, so the source pill (PO / manual / preorder) moved into the hover
 *    tooltip to buy back the width (Rhys 2026-08-29).
 */
function formatDisplayDate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC'
  });
}

function ProductEtaRow({ product, darkMode, canEdit = true }) {
  const channel = restockChannelForProduct(product);
  const sku = product?.sku;
  const [entry, setEntry] = useState(undefined); // undefined=loading, null=no date
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!channel || !sku || sku === 'N/A') { setEntry(null); return undefined; }
    getRestockDate(channel, sku).then((result) => { if (!cancelled) setEntry(result); });
    return () => { cancelled = true; };
  }, [channel, sku]);

  if (!channel || !sku || sku === 'N/A') return null;

  // Local date, not toISOString (UTC) — a today-dated ETA read before ~10am
  // Sydney is still "yesterday" in UTC and wrongly read as past.
  const now = new Date();
  const todayLocal = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const isPast = Boolean(entry?.date && entry.date < todayLocal);
  const stockQty = Number(product?.stockOnHand) || 0;

  // Website-style relevance (Rhys 2026-08-24): once stock is on hand a past
  // ETA is dead information — hide the row entirely. Out of stock, a past
  // date only stays visible (red) when an ACTIVE PO is behind it, as the
  // cue to update that PO's ETA; a lapsed manual/preorder estimate renders
  // as unset so staff set a fresh one.
  if (entry && isPast && stockQty > 0) return null;
  const lapsedNonPo = Boolean(entry && isPast && entry.source !== 'po');

  // canEdit mirrors the setEtaDates task permission (the server enforces it
  // regardless); PO/preorder-sourced dates stay read-only here by design —
  // except a lapsed one, which behaves as unset.
  const editableHere = canEdit && (!entry || entry.source === 'manual' || lapsedNonPo);
  // A lapsed estimate is presented as unset, so it must not seed the calendar.
  const shownDate = entry && !lapsedNonPo ? entry.date : '';

  // A past date lapses the moment it is saved and the row redraws as unset,
  // which reads as "the save did nothing" — floor the calendar at today.
  const todayFloor = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // The source pill used to say this on the card. It now lives here, because
  // the row shares a line with the variant label and has no width to spare.
  const sourceNote = !shownDate
    ? 'No date set yet.'
    : entry.source === 'po'
      ? 'Set by a purchase order. Change it on the Incoming Stock card.'
      : entry.source === 'preorder'
        ? 'Set by a preorder line. Change it in Preorders.'
        : 'Manual staff estimate. A real PO date always overrides it, and estimates lapse once the date passes.';
  const publishNote = shownDate
    ? (entry.published
      ? ' Live on the Shopify Estimated Restock Date metafield.'
      : ' Not published to Shopify yet.')
    : '';
  const actionNote = !canEdit
    ? ' Editing needs the "Set ETA dates" permission, ask an admin.'
    : editableHere
      ? (shownDate ? ' Click the date to change it.' : ' Click to set a manual estimate.')
      : '';
  const rowTitle = `Customer-facing restock date. ${sourceNote}${publishNote}${actionNote}`;

  const save = async (date) => {
    if (!date) return;
    setSaving(true);
    setError('');
    try {
      setEntry(await setManualRestockEstimate(channel, sku, date));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save ETA');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    setError('');
    try {
      await clearManualRestockEstimate(channel, sku);
      setEntry(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clear ETA');
    } finally {
      setSaving(false);
    }
  };

  // fit-content: the row only borders what it shows instead of a full-width
  // box with dead space (Rhys 2026-08-24).
  const rowStyle = {
    display: 'inline-flex', alignItems: 'center', gap: '7px',
    width: 'fit-content', maxWidth: '100%',
    padding: '3px 10px', fontSize: '13px', minHeight: '28px',
    border: '1px solid var(--app-border)', borderRadius: '7px',
    background: 'var(--app-surface-2)',
    cursor: 'default'
  };
  // Future/today = green (on track); past + active PO = red (update the PO).
  const dateColor = entry?.date
    ? (!isPast ? (darkMode ? '#4ade80' : '#16a34a') : (darkMode ? '#f87171' : '#dc2626'))
    : undefined;
  const dateTextStyle = { fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: dateColor };
  const unsetTextStyle = { color: '#9ca3af', fontSize: '11px' };

  // Source at a glance, standing in for the pill that used to eat the width:
  // teal = PO (authoritative), amber = manual estimate, violet = preorder
  // line. Same per-mode colours the pills used. Colour alone is never the
  // only carrier — the row tooltip names the source in words (Rhys 2026-08-29).
  const sourceDotColor = {
    po: darkMode ? '#5eead4' : '#0d9488',
    manual: darkMode ? '#fcd34d' : '#b45309',
    preorder: darkMode ? '#c4b5fd' : '#6d28d9'
  };
  const sourceDot = shownDate && entry.source === 'manual' ? (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block', verticalAlign: 'middle',
        width: '7px', height: '7px', borderRadius: '50%',
        marginRight: '5px', flexShrink: 0,
        background: sourceDotColor[entry.source] || sourceDotColor.preorder
      }}
    />
  ) : null;

  return (
    <div
      // The calendar is portalled to <body>, but React still bubbles its clicks
      // up THIS tree — contain them so picking a day cannot reach the card's
      // own click handlers.
      onClick={(e) => e.stopPropagation()}
      style={rowStyle}
      title={rowTitle}
    >
      <strong>ETA:</strong>
      {entry === undefined ? (
        <span style={unsetTextStyle}>…</span>
      ) : editableHere ? (
        <IsoDateCalendarPicker
          value={shownDate}
          onChange={save}
          busy={saving}
          darkMode={darkMode}
          disableDatesBefore={todayFloor}
          title={shownDate ? 'Change the estimated restock date' : 'Set an estimated restock date'}
          activatorLabel={shownDate ? <>{sourceDot}{formatDisplayDate(shownDate)}</> : 'click to set'}
          activatorStyle={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: 0, border: 'none', background: 'transparent',
            font: 'inherit', ...(shownDate ? dateTextStyle : unsetTextStyle)
          }}
        />
      ) : shownDate ? (
        <span style={dateTextStyle}>{sourceDot}{formatDisplayDate(shownDate)}</span>
      ) : (
        <span style={unsetTextStyle}>click to set</span>
      )}
      {shownDate && entry.published && entry.source === 'manual' && (
        // Bare tick, no pill: the row shares a line with the variant label.
        // The tooltip above spells out what it means.
        <span
          aria-label="Live on Shopify"
          style={{ fontWeight: 700, color: darkMode ? '#86efac' : '#16a34a' }}
        >✓</span>
      )}
      {shownDate && canEdit && entry.source === 'manual' && !saving && (
        <span
          role="button"
          tabIndex={0}
          title="Clear this manual estimate"
          onClick={(e) => { e.stopPropagation(); clear(); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); clear(); } }}
          style={{ cursor: 'pointer', opacity: 0.6, fontWeight: 700 }}
        >×</span>
      )}
      {error && <span style={{ color: '#dc2626', fontSize: '10px' }}>{error}</span>}
    </div>
  );
}

export default ProductEtaRow;
